import { DataPlaneError, isTransferError, type TransferError } from "@istari/istari-client";

export function findTransferError(error: unknown): TransferError | DataPlaneError | undefined {
  let current = error;
  for (let depth = 0; depth < 8; depth++) {
    if (isTransferError(current) || current instanceof DataPlaneError) return current;
    if (!isRecord(current)) return undefined;
    current = current.cause;
  }
  return undefined;
}

export function isTransferCancelled(error: unknown): boolean {
  let current = error;
  for (let depth = 0; depth < 8; depth++) {
    if (!isRecord(current)) return false;
    if (current.name === "AbortError" || current.name === "TransferCancelledError") return true;
    current = current.cause;
  }
  return false;
}

export function describeTransferError(error: unknown): string | undefined {
  const transferError = findTransferError(error);
  if (!transferError) return undefined;
  switch (transferError.name) {
    case "TransferCancelledError":
      return "Transfer cancelled.";
    case "TransferFailedError":
      return "Transfer could not finish after retrying. Try again.";
    case "ContentVerificationError":
      return "Content verification failed. The file was not accepted.";
    case "FileChangedDuringUploadError":
      return "The selected file does not match the original upload.";
    case "ContentTooLargeError":
      return "This operation cannot buffer a file this large.";
    case "PartPlanError":
      return "The file transfer could not be planned.";
    default:
      return "The file transfer failed.";
  }
}

// Format an SDK error for user-facing display. Prefers the server's `{ detail }`
// body so we surface the real reason; falls back to the caller's generic
// message. Avoid concatenating `error.message` directly — the SDK ResponseError
// message embeds the full request URL and HTTP status, which reads as noise.
export async function describeSdkError(error: unknown, fallback: string): Promise<string> {
  const detail = await extractSdkErrorDetail(error, { joinValidationDetail: true }).catch(() => undefined);
  return detail ?? fallback;
}

type ExtractSdkErrorDetailOptions = {
  joinValidationDetail?: boolean;
};

// Best-effort extraction of the `detail` message from an SDK ResponseError body.
// The V3 SDK throws errors that carry the original Response on `error.response`; most
// backend errors serialize as `{ detail: "..." }`, coded ones (e.g. 409s) as
// `{ detail: { error_code, message } }`. We clone the response before reading
// so the original body stream stays intact for anyone else inspecting the error.
export async function extractSdkErrorDetail(
  error: unknown,
  options: ExtractSdkErrorDetailOptions = {},
): Promise<string | undefined> {
  const transferDetail = describeTransferError(error);
  if (transferDetail) return transferDetail;
  if (!isRecord(error) || !(error.response instanceof Response)) {
    return isRecord(error) && error.cause !== error ? extractSdkErrorDetailFromCause(error.cause, options) : undefined;
  }
  try {
    const body: unknown = await error.response.clone().json();
    if (isRecord(body) && typeof body.detail === "string" && body.detail.trim()) return body.detail;
    if (
      isRecord(body) &&
      isRecord(body.detail) &&
      typeof body.detail.message === "string" &&
      body.detail.message.trim()
    ) {
      return body.detail.message;
    }
    if (options.joinValidationDetail && isRecord(body) && Array.isArray(body.detail)) {
      const messages = body.detail
        .map((item) => (isRecord(item) && typeof item.msg === "string" ? item.msg : null))
        .filter((message): message is string => message !== null);
      if (messages.length > 0) return messages.join(" ");
    }
  } catch {
    // non-JSON body; fall through.
  }
  return undefined;
}

/** The `detail.error_code` of a 409 refusing a commit because the branch moved since the caller read it. */
export const STALE_SYSTEM_HEAD = "STALE_SYSTEM_HEAD";

/** The `detail.error_code` of a 409 refusing a direct write because the branch is protected. */
export const PROTECTED_BRANCH = "PROTECTED_BRANCH";

/** The `detail.error_code` of a 409 refusing a tag create or rename because another tag in the system uses the name. */
export const TAG_NAME_TAKEN = "TAG_NAME_TAKEN";

/** The `detail.error_code` of a 409 refusing a change request merge because it lacks the required approvals. */
export const APPROVAL_REQUIRED = "APPROVAL_REQUIRED";

/** The `detail.error_code` of a 409 refusing a change request merge because its source or target moved since it was read. */
export const STALE_HEAD_PAIR = "STALE_HEAD_PAIR";

/**
 * A 409 names its cause in `detail.error_code`, with the explanation beside it in `detail.message`.
 * `fields` is the raw `detail` record, for code-specific values the caller reads with its own checks.
 */
export async function readConflictDetail(
  error: unknown,
): Promise<{ errorCode?: string; message?: string; fields: Record<string, unknown> } | undefined> {
  if (!isRecord(error) || !(error.response instanceof Response)) return undefined;
  try {
    const body: unknown = await error.response.clone().json();
    if (!isRecord(body) || !isRecord(body.detail)) return undefined;
    const { error_code: errorCode, message } = body.detail;
    return {
      errorCode: typeof errorCode === "string" ? errorCode : undefined,
      message: typeof message === "string" ? message : undefined,
      fields: body.detail,
    };
  } catch {
    return undefined;
  }
}

// Synchronous HTTP status extraction — duck-types `{ response: { status: number } }`
// which is the shape thrown by both the V2 and V3 SDKs.
export function getResponseStatus(error: unknown): number | undefined {
  let current = error;
  for (let depth = 0; depth < 8; depth++) {
    if (!isRecord(current)) return undefined;
    if (isRecord(current.response)) {
      const { status } = current.response;
      if (typeof status === "number" && Number.isInteger(status) && status >= 100 && status <= 599) return status;
    }
    current = current.cause;
  }
  return undefined;
}

/** True when the error chain includes HTTP 401 (session expired / unauthenticated). */
export function isUnauthorizedResponse(error: unknown): boolean {
  return getResponseStatus(error) === 401;
}

/** True when the error chain includes HTTP 403 (authenticated but lacking permission). */
export function isForbiddenResponse(error: unknown): boolean {
  return getResponseStatus(error) === 403;
}

/** True for either 401 or 403 in the bounded SDK error cause chain. */
export function isAccessDeniedResponse(error: unknown): boolean {
  const status = getResponseStatus(error);
  return status === 401 || status === 403;
}

/** Maps an SDK error to an access-denied status, for building field/query state unions. */
export function resolveAccessDeniedStatus(error: unknown): "unauthorized" | "forbidden" | "error" {
  if (isUnauthorizedResponse(error)) {
    return "unauthorized";
  }
  if (isForbiddenResponse(error)) {
    return "forbidden";
  }
  return "error";
}

// 408 (Request Timeout), 425 (Too Early), and 429 (Too Many Requests) are transient —
// a retry can succeed. Everything else in 4xx is a definitive client failure.
const TRANSIENT_CLIENT_STATUSES = new Set([408, 425, 429]);

export function isNonRetryableClientError(error: unknown): boolean {
  if (findTransferError(error)) return true;
  const status = getResponseStatus(error);
  if (status === undefined) return false;
  if (TRANSIENT_CLIENT_STATUSES.has(status)) return false;
  return status >= 400 && status < 500;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function extractSdkErrorDetailFromCause(
  error: unknown,
  options: ExtractSdkErrorDetailOptions,
): Promise<string | undefined> {
  let current = error;
  for (let depth = 0; depth < 8; depth++) {
    if (!isRecord(current)) return undefined;
    if (current.response instanceof Response) return extractSdkErrorDetail(current, options);
    current = current.cause;
  }
  return undefined;
}
